import React, { useMemo, useState } from "react";
import moment from "moment";
import { useQuery, useQueryClient } from "react-query";
import { getErrorMessage } from "services/api";

import { Button } from "components/Button";
import { Input } from "components/Input";
import { Checkbox } from "components/Checkbox";
import { Calendar } from "components/Calendar";
import { Table } from "components/Table";
import { MediumText, RegularText } from "components/Text";
import { VerticalSpace } from "components/Spacing";

import { getClientDates, createClientDate, deleteClientDate } from "./api";
import styles from "./styles.module.scss";

const yesNoCell = (params) => (Number(params.value) ? "Да" : "Нет");

const DATE_LABEL = 160;

/** Знаменательные даты клиента (только для существующего клиента) */
export const ClientDatesSection = ({ clientid }) => {
  const queryClient = useQueryClient();

  const datesQuery = useQuery(["client-dates", clientid], () =>
    getClientDates(clientid)
  );
  const dates = useMemo(() => datesQuery.data?.items ?? [], [datesQuery.data]);

  const [selectedId, setSelectedId] = useState(null);
  const [showAddFields, setShowAddFields] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: "",
    date: null,
    bonus: "",
    plusbonus: "",
    pluspercent: "",
    radiusminus: "",
    radiusplus: "",
    burnday: false,
    useonce: false,
    domessage: false,
    rememberday: "",
  });
  const patch = (update) => setForm((prev) => ({ ...prev, ...update }));

  const rowData = useMemo(
    () =>
      dates.map((d) => ({
        ...d,
        dateFormatted: d.date ? moment(d.date).format("DD.MM.YYYY") : "",
      })),
    [dates]
  );

  const colDefs = useMemo(
    () => [
      { field: "clientdateid", headerName: "№", width: 70 },
      { field: "name", headerName: "Название", width: 140 },
      { field: "dateFormatted", headerName: "Дата", width: 110 },
      { field: "bonus", headerName: "Бонус", width: 80 },
      { field: "plusbonus", headerName: "Увеличить % бонуса", width: 150 },
      { field: "pluspercent", headerName: "Скидка %", width: 90 },
      { field: "radiusminus", headerName: "Действуют до, дни", width: 140 },
      { field: "radiusplus", headerName: "Действуют после, дни", width: 150 },
      {
        field: "burnday",
        headerName: "Сгорают",
        width: 90,
        cellRenderer: yesNoCell,
      },
      {
        field: "useonce",
        headerName: "Начисляются 1 раз",
        width: 140,
        cellRenderer: yesNoCell,
      },
      {
        field: "domessage",
        headerName: "Рассылка",
        width: 95,
        cellRenderer: yesNoCell,
      },
      { field: "rememberday", headerName: "Напоминать за, дни", width: 150 },
    ],
    []
  );

  const handleSave = async () => {
    if (!form.name || !form.date) return;
    setSaving(true);
    setError("");
    try {
      // Компенсация сдвига таймзоны, чтобы на сервере остался тот же день
      const date = new Date(form.date);
      date.setHours(date.getHours() - date.getTimezoneOffset() / 60);

      await createClientDate({
        clientid: Number(clientid),
        name: form.name,
        date: date.toISOString(),
        bonus: Number(form.bonus) || 0,
        plusbonus: Number(form.plusbonus) || 0,
        pluspercent: Number(form.pluspercent) || 0,
        plusdiscount: Number(form.pluspercent) || 0,
        radiusminus: Number(form.radiusminus) || 0,
        radiusplus: Number(form.radiusplus) || 0,
        burnday: form.burnday ? 1 : 0,
        useonce: form.useonce ? 1 : 0,
        domessage: form.domessage ? 1 : 0,
        smstextid: "1",
        rememberday: Number(form.rememberday) || 0,
      });
      setShowAddFields(false);
      setForm({
        name: "",
        date: null,
        bonus: "",
        plusbonus: "",
        pluspercent: "",
        radiusminus: "",
        radiusplus: "",
        burnday: false,
        useonce: false,
        domessage: false,
        rememberday: "",
      });
      queryClient.invalidateQueries(["client-dates", clientid]);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    setError("");
    try {
      await deleteClientDate(selectedId);
      setSelectedId(null);
      queryClient.invalidateQueries(["client-dates", clientid]);
    } catch (e) {
      setError(getErrorMessage(e));
    }
  };

  return (
    <div>
      <MediumText>Знаменательные даты</MediumText>
      <VerticalSpace size={8} />
      <Table
        height={220}
        rowData={rowData}
        colDefs={colDefs}
        rowKey="clientdateid"
        selectedId={selectedId}
        onSelect={(row) =>
          setSelectedId((prev) =>
            prev === row.clientdateid ? null : row.clientdateid
          )
        }
        loading={datesQuery.isLoading}
        error={datesQuery.isError ? getErrorMessage(datesQuery.error) : ""}
        onRetry={datesQuery.refetch}
      />
      <VerticalSpace size={8} />
      {showAddFields && (
        <div className={styles.addDateFields}>
          <div className={styles.addDateGrid}>
            <Input
              label="Название"
              labelWidth={DATE_LABEL}
              value={form.name}
              onChange={(ev) => patch({ name: ev.target.value })}
              inputWidth={160}
            />
            <Calendar
              label="Дата"
              labelWidth={DATE_LABEL}
              inputWidth={160}
              value={form.date}
              onChange={(val) => patch({ date: val })}
            />
            <Input
              label="Бонус"
              labelWidth={DATE_LABEL}
              type="number"
              value={form.bonus}
              onChange={(ev) => patch({ bonus: ev.target.value })}
              inputWidth={160}
            />
            <Input
              label="Увеличить % бонуса"
              labelWidth={DATE_LABEL}
              type="number"
              value={form.plusbonus}
              onChange={(ev) => patch({ plusbonus: ev.target.value })}
              inputWidth={160}
            />
            <Input
              label="Скидка %"
              labelWidth={DATE_LABEL}
              type="number"
              value={form.pluspercent}
              onChange={(ev) => patch({ pluspercent: ev.target.value })}
              inputWidth={160}
            />
            <Input
              label="Действуют до, дни"
              labelWidth={DATE_LABEL}
              type="number"
              value={form.radiusminus}
              onChange={(ev) => patch({ radiusminus: ev.target.value })}
              inputWidth={160}
            />
            <Input
              label="Действуют после, дни"
              labelWidth={DATE_LABEL}
              type="number"
              value={form.radiusplus}
              onChange={(ev) => patch({ radiusplus: ev.target.value })}
              inputWidth={160}
            />
            <Input
              label="Напоминать за, дни"
              labelWidth={DATE_LABEL}
              type="number"
              value={form.rememberday}
              onChange={(ev) => patch({ rememberday: ev.target.value })}
              inputWidth={160}
            />
          </div>
          <div className={styles.addDateChecks}>
            <Checkbox
              label="Сгорают"
              checked={form.burnday}
              onChange={(val) => patch({ burnday: val })}
            />
            <Checkbox
              label="Начисляются 1 раз"
              checked={form.useonce}
              onChange={(val) => patch({ useonce: val })}
            />
            <Checkbox
              label="Рассылка"
              checked={form.domessage}
              onChange={(val) => patch({ domessage: val })}
            />
          </div>
        </div>
      )}
      {!!error && (
        <RegularText size={13} color="danger">
          {error}
        </RegularText>
      )}
      <VerticalSpace size={8} />
      <div className={styles.sectionActions}>
        {showAddFields ? (
          <Button
            icon="galochka"
            variant="secondary"
            onClick={handleSave}
            disabled={!form.name || !form.date}
            loading={saving}
          >
            Сохранить
          </Button>
        ) : (
          <Button icon="plus" onClick={() => setShowAddFields(true)}>
            Добавить
          </Button>
        )}
        <Button
          icon="delete"
          onClick={handleDelete}
          disabled={!selectedId}
          title="Удалить дату"
        />
      </div>
    </div>
  );
};
